import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  createCollection,
  updateCollection,
  addProductsToCollection,
  removeProductFromCollection,
  fetchCollection,
  selectCurrentCollection
} from './collectionsSlice';
import { useToast } from '../../components/ui/Toast';
import { apiFetch } from '../../utils/api';
import './CollectionModal.css';

const CollectionModal = ({ collection, onClose }) => {
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const currentCollection = useSelector(selectCurrentCollection);

  const [activeTab, setActiveTab] = useState('details');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    name: collection?.name || '',
    description: collection?.description || '',
    collectionType: collection?.collectionType || 'MANUAL',
    isPublic: collection?.isPublic !== false,
    featured: collection?.featured || false,
    image: collection?.image || '',
    coverImage: collection?.coverImage || '',
    rules: collection?.rules || {}
  });

  const [uploadingImage, setUploadingImage] = useState(false);

  // Selected products for manual collection
  const [selectedProductIds, setSelectedProductIds] = useState([]);

  // Load collection details if editing
  useEffect(() => {
    if (collection?.id) {
      dispatch(fetchCollection(collection.id));
    }
  }, [collection?.id, dispatch]);

  // Update selected products when collection data loads
  useEffect(() => {
    if (currentCollection?.products) {
      setSelectedProductIds(currentCollection.products.map(p => p.product?.id || p.productId));
    }
  }, [currentCollection]);

  // Load products for selection
  useEffect(() => {
    const loadProducts = async () => {
      setLoadingProducts(true);
      try {
        const response = await apiFetch('/merchants/products?limit=200&status=ACTIVE');
        const data = await response.json();
        setProducts(data.data?.products || data.products || []);
      } catch (error) {
        console.error('Failed to load products:', error);
      } finally {
        setLoadingProducts(false);
      }
    };

    if (activeTab === 'products' && formData.collectionType === 'MANUAL') {
      loadProducts();
    }
  }, [activeTab, formData.collectionType]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleCoverImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      showToast('Please select an image file', 'error');
      return;
    }

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      showToast('Image must be less than 5MB', 'error');
      return;
    }

    setUploadingImage(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('image', file);

      const response = await apiFetch('/upload', {
        method: 'POST',
        body: uploadFormData
      });

      const data = await response.json();
      if (data.url) {
        setFormData(prev => ({ ...prev, coverImage: data.url }));
        showToast('Cover image uploaded', 'success');
      } else {
        throw new Error('No URL returned');
      }
    } catch (error) {
      console.error('Failed to upload image:', error);
      showToast('Failed to upload image', 'error');
    } finally {
      setUploadingImage(false);
    }
  };

  const removeCoverImage = () => {
    setFormData(prev => ({ ...prev, coverImage: '' }));
  };

  const handleRulesChange = (ruleType, value) => {
    setFormData(prev => ({
      ...prev,
      rules: {
        type: ruleType,
        ...value
      }
    }));
  };

  const toggleProductSelection = (productId) => {
    setSelectedProductIds(prev =>
      prev.includes(productId)
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const selectAllProducts = () => {
    const filteredIds = filteredProducts.map(p => p.id);
    setSelectedProductIds(prev => [...new Set([...prev, ...filteredIds])]);
  };

  const clearAllProducts = () => {
    setSelectedProductIds([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      showToast('Collection name is required', 'error');
      return;
    }

    setIsSubmitting(true);

    try {
      if (collection?.id) {
        // Update existing collection
        await dispatch(updateCollection({
          id: collection.id,
          data: formData
        })).unwrap();

        // Update products for manual collections
        if (formData.collectionType === 'MANUAL') {
          const currentProductIds = currentCollection?.products?.map(p => p.product?.id || p.productId) || [];
          const toAdd = selectedProductIds.filter(id => !currentProductIds.includes(id));
          const toRemove = currentProductIds.filter(id => !selectedProductIds.includes(id));

          // Add new products
          if (toAdd.length > 0) {
            await dispatch(addProductsToCollection({
              collectionId: collection.id,
              productIds: toAdd
            })).unwrap();
          }

          // Remove products
          for (const productId of toRemove) {
            await dispatch(removeProductFromCollection({
              collectionId: collection.id,
              productId
            })).unwrap();
          }
        }

        showToast('Collection updated successfully', 'success');
      } else {
        // Create new collection
        const result = await dispatch(createCollection({
          ...formData,
          productIds: formData.collectionType === 'MANUAL' ? selectedProductIds : []
        })).unwrap();

        showToast('Collection created successfully', 'success');
      }

      onClose();
    } catch (error) {
      showToast(error || 'Failed to save collection', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.category?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="collection-modal-overlay" onClick={onClose}>
      <div className="collection-modal" onClick={e => e.stopPropagation()}>
        <div className="collection-modal-header">
          <h2>{collection ? 'Edit Collection' : 'New Collection'}</h2>
          <button className="collection-modal-close" onClick={onClose}>
            <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="collection-modal-tabs">
          <div className="collection-modal-tabs-inner">
            <button
              className={`collection-modal-tab ${activeTab === 'details' ? 'active' : ''}`}
              onClick={() => setActiveTab('details')}
            >
              Details
            </button>
            {formData.collectionType === 'MANUAL' && (
              <button
                className={`collection-modal-tab ${activeTab === 'products' ? 'active' : ''}`}
                onClick={() => setActiveTab('products')}
              >
                Products ({selectedProductIds.length})
              </button>
            )}
            {formData.collectionType === 'AUTOMATIC' && (
              <button
                className={`collection-modal-tab ${activeTab === 'rules' ? 'active' : ''}`}
                onClick={() => setActiveTab('rules')}
              >
                Rules
              </button>
            )}
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="collection-modal-content">
            {activeTab === 'details' && (
              <div className="collection-form-details">
                <div className="collection-form-group">
                  <label htmlFor="name">Collection Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="e.g., Summer Sale, Best Sellers"
                    required
                  />
                </div>

                <div className="collection-form-group">
                  <label htmlFor="description">Description</label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Brief description of this collection"
                    rows={3}
                  />
                </div>

                <div className="collection-form-group">
                  <label htmlFor="collectionType">Collection Type</label>
                  <select
                    id="collectionType"
                    name="collectionType"
                    value={formData.collectionType}
                    onChange={handleInputChange}
                  >
                    <option value="MANUAL">Manual - Hand-pick products</option>
                    <option value="AUTOMATIC">Automatic - Rule-based</option>
                  </select>
                  <small className="collection-form-hint">
                    {formData.collectionType === 'MANUAL'
                      ? 'Manually select which products belong to this collection'
                      : 'Products are automatically added based on rules you set'}
                  </small>
                </div>

                <div className="collection-form-group">
                  <label>Cover Image</label>
                  <div className="collection-cover-upload">
                    {formData.coverImage ? (
                      <div className="collection-cover-preview">
                        <img src={formData.coverImage} alt="Cover" />
                        <button
                          type="button"
                          className="collection-cover-remove"
                          onClick={removeCoverImage}
                          title="Remove image"
                        >
                          <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ) : (
                      <label className="collection-cover-dropzone">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleCoverImageUpload}
                          disabled={uploadingImage}
                        />
                        {uploadingImage ? (
                          <>
                            <div className="loading-spinner small"></div>
                            <span>Uploading...</span>
                          </>
                        ) : (
                          <>
                            <svg width="32" height="32" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                              <rect x="3" y="3" width="18" height="18" rx="2" />
                              <path d="m3 15 6-6 4 4 8-8" />
                              <circle cx="8.5" cy="8.5" r="1.5" />
                            </svg>
                            <span>Click to upload cover image</span>
                            <small>Recommended: 1200 × 400px</small>
                          </>
                        )}
                      </label>
                    )}
                  </div>
                  <small className="collection-form-hint">
                    This image appears at the top of your collection page
                  </small>
                </div>

                <div className="collection-form-row">
                  <div className="collection-form-group collection-form-checkbox">
                    <label>
                      <input
                        type="checkbox"
                        name="isPublic"
                        checked={formData.isPublic}
                        onChange={handleInputChange}
                      />
                      <span>Public</span>
                    </label>
                    <small>Visible to customers on your storefront</small>
                  </div>

                  <div className="collection-form-group collection-form-checkbox">
                    <label>
                      <input
                        type="checkbox"
                        name="featured"
                        checked={formData.featured}
                        onChange={handleInputChange}
                      />
                      <span>Featured</span>
                    </label>
                    <small>Highlight this collection on your storefront</small>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'products' && formData.collectionType === 'MANUAL' && (
              <div className="collection-form-products">
                <div className="collection-products-search">
                  <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <circle cx="11" cy="11" r="8" />
                    <path d="m21 21-4.35-4.35" />
                  </svg>
                  <input
                    type="text"
                    placeholder="Search products..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>

                <div className="collection-products-actions">
                  <button type="button" onClick={selectAllProducts} className="collection-products-action-btn">
                    Select All
                  </button>
                  <button type="button" onClick={clearAllProducts} className="collection-products-action-btn">
                    Clear All
                  </button>
                  <span className="collection-products-count">
                    {selectedProductIds.length} selected
                  </span>
                </div>

                {loadingProducts ? (
                  <div className="collection-products-loading">
                    <div className="loading-spinner"></div>
                    <p>Loading products...</p>
                  </div>
                ) : (
                  <div className="collection-products-list">
                    {filteredProducts.map(product => (
                      <label
                        key={product.id}
                        className={`collection-product-item ${selectedProductIds.includes(product.id) ? 'selected' : ''}`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedProductIds.includes(product.id)}
                          onChange={() => toggleProductSelection(product.id)}
                        />
                        <div className="collection-product-image">
                          {product.primaryImage ? (
                            <img src={product.primaryImage} alt={product.name} />
                          ) : (
                            <div className="collection-product-no-image">
                              <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                                <rect x="3" y="3" width="18" height="18" rx="2" />
                                <path d="m3 15 6-6 4 4 8-8" />
                              </svg>
                            </div>
                          )}
                        </div>
                        <div className="collection-product-info">
                          <span className="collection-product-name">{product.name}</span>
                          <span className="collection-product-meta">
                            {product.category} • ₦{Number(product.price).toLocaleString()}
                          </span>
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'rules' && formData.collectionType === 'AUTOMATIC' && (
              <div className="collection-form-rules">
                <div className="collection-form-group">
                  <label>Rule Type</label>
                  <select
                    value={formData.rules?.type || ''}
                    onChange={(e) => handleRulesChange(e.target.value, {})}
                  >
                    <option value="">Select a rule type</option>
                    <option value="category">By Category</option>
                    <option value="tag">By Tags</option>
                    <option value="flags">By Product Flags</option>
                    <option value="price_range">By Price Range</option>
                  </select>
                </div>

                {formData.rules?.type === 'category' && (
                  <div className="collection-form-group">
                    <label>Category Name</label>
                    <input
                      type="text"
                      value={formData.rules?.value || ''}
                      onChange={(e) => handleRulesChange('category', { value: e.target.value })}
                      placeholder="e.g., perfumes, shoes, clothing"
                    />
                  </div>
                )}

                {formData.rules?.type === 'tag' && (
                  <div className="collection-form-group">
                    <label>Tags (comma-separated)</label>
                    <input
                      type="text"
                      value={formData.rules?.values?.join(', ') || ''}
                      onChange={(e) => handleRulesChange('tag', {
                        values: e.target.value.split(',').map(t => t.trim()).filter(Boolean)
                      })}
                      placeholder="e.g., summer, sale, new"
                    />
                  </div>
                )}

                {formData.rules?.type === 'flags' && (
                  <div className="collection-form-group">
                    <label>Product Flags</label>
                    <div className="collection-rules-flags">
                      <label>
                        <input
                          type="checkbox"
                          checked={formData.rules?.featured || false}
                          onChange={(e) => handleRulesChange('flags', { ...formData.rules, featured: e.target.checked })}
                        />
                        <span>Featured</span>
                      </label>
                      <label>
                        <input
                          type="checkbox"
                          checked={formData.rules?.bestSeller || false}
                          onChange={(e) => handleRulesChange('flags', { ...formData.rules, bestSeller: e.target.checked })}
                        />
                        <span>Best Seller</span>
                      </label>
                      <label>
                        <input
                          type="checkbox"
                          checked={formData.rules?.newArrival || false}
                          onChange={(e) => handleRulesChange('flags', { ...formData.rules, newArrival: e.target.checked })}
                        />
                        <span>New Arrival</span>
                      </label>
                      <label>
                        <input
                          type="checkbox"
                          checked={formData.rules?.trending || false}
                          onChange={(e) => handleRulesChange('flags', { ...formData.rules, trending: e.target.checked })}
                        />
                        <span>Trending</span>
                      </label>
                    </div>
                  </div>
                )}

                {formData.rules?.type === 'price_range' && (
                  <div className="collection-form-row">
                    <div className="collection-form-group">
                      <label>Min Price</label>
                      <input
                        type="number"
                        value={formData.rules?.min || ''}
                        onChange={(e) => handleRulesChange('price_range', {
                          ...formData.rules,
                          min: e.target.value ? Number(e.target.value) : undefined
                        })}
                        placeholder="0"
                      />
                    </div>
                    <div className="collection-form-group">
                      <label>Max Price</label>
                      <input
                        type="number"
                        value={formData.rules?.max || ''}
                        onChange={(e) => handleRulesChange('price_range', {
                          ...formData.rules,
                          max: e.target.value ? Number(e.target.value) : undefined
                        })}
                        placeholder="No limit"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="collection-modal-footer">
            <button
              type="button"
              className="collection-modal-btn collection-modal-btn-secondary"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="collection-modal-btn collection-modal-btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="btn-spinner"></span>
                  Saving...
                </>
              ) : (
                collection ? 'Save Changes' : 'Create Collection'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CollectionModal;
